import { FormEvent, useState } from "react";
import { Loader, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface KnowledgeChunk {
  uuid: string;
  document_id: string;
  document_name: string;
  content: string;
  chunk_number: number;
  vectorized: boolean;
  document_page: number;
  similarity_score: number;
}

interface BackendRagResponse {
  status: string;
  query: string;
  chunks: KnowledgeChunk[];
}

export const RagTest = () => {
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState<BackendRagResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const encodedQuery = encodeURIComponent(query.trim());
      const url = `${import.meta.env.VITE_BACKEND_BASE_URL}/get_knowledge_chunk?query=${encodedQuery}`;
      console.log("Making request to:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "text/plain",
        },
      });

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const backendResponse = await response.json();
      console.log("Response data:", backendResponse);
      setResponse(backendResponse);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Test Retrieval</h1>
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
          onClick={() => navigate("/coordinator/knowledge-manager")}
        >
          Back to Knowledge Manager
        </button>
      </div>

      <div className="rounded-xl border bg-white shadow-sm p-6 mb-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Query
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter your query here..."
                className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="bg-[#4CAF50] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#45a049] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="animate-spin size-4" />
                Searching...
              </>
            ) : (
              "Search Knowledge Base"
            )}
          </button>
        </form>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-6">
          <p className="text-red-700 font-medium">Error: {error}</p>
        </div>
      )}

      {response && (
        <div className="rounded-xl border bg-white shadow-sm p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Query: "{response.query}"
            </h2>
            <p className="text-sm text-gray-600">
              Found {response.chunks.length} relevant chunks
            </p>
          </div>

          <div className="space-y-4">
            {response.chunks.map((chunk, index) => (
              <div
                key={chunk.uuid}
                className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                      Chunk {chunk.chunk_number}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    Document Name: {chunk.document_name}
                  </span>
                  <span className="text-xs text-gray-400">
                    Page in Document: {chunk.document_page}
                  </span>
                  <span className="text-xs text-gray-400">
                    Similarity Score: {chunk.similarity_score.toFixed(2)}
                  </span>
                </div>
                <pre className="text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">
                  {chunk.content}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RagTest;
